'use client';

import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { TYPOGRAPHY } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { media } from '@/constants/breakpoints';
import { getProductImageUrl, handleImageError } from '@/utils/imageHelpers';
import { formatPriceWithCurrency } from '@/utils/formatters';
import Button1 from './Button1';
import AddToCart from './AddToCart';
import LazySection from './LazySection';
import type { FilterProduct } from '@/types/api';

interface FilterCardGridProps {
  tableData: FilterProduct[];
  diameterField?: string;
  heightField?: string;
  styleField?: string;
  colorField?: string;
  /** Outside diameter at the base of a tapered cone/cylinder filter. Optional since
   * not every filter family (e.g. flat panel filters) has a base/top OD distinction. */
  odBaseField?: string;
  /** Outside diameter at the top of a tapered cone/cylinder filter. */
  odTopField?: string;
  /** Single outside diameter, for non-tapered filters (e.g. round) that have one OD instead of a base/top pair. */
  odField?: string;
  defaultSortBy?: SortOption;
}

type SortOption = 'name-asc' | 'price-asc' | 'price-desc' | 'height-asc' | 'diameter-asc' | 'od-asc';

const SORT_LABELS: Record<SortOption, string> = {
  'name-asc': 'Name (A-Z)',
  'price-asc': 'Price (Low to High)',
  'price-desc': 'Price (High to Low)',
  'height-asc': 'Height (Low to High)',
  'diameter-asc': 'Inlet Diameter (Low to High)',
  'od-asc': 'Outside Diameter (Low to High)',
};

const ALL_VALUE = 'All';
const MAX_VISIBLE_COLORS = 2;

function parseInches(value: unknown): number {
  return parseFloat(String(value ?? '').replace(/[^0-9.]/g, '')) || 0;
}

function uniqueTrimmedValues(rows: FilterProduct[], field: string): string[] {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = String(row[field] ?? '').trim();
    if (value) {
      values.add(value);
    }
  });
  return Array.from(values).sort();
}

const FilterCardGrid: React.FC<FilterCardGridProps> = ({
  tableData,
  diameterField = 'filter_id_length_inch',
  heightField = 'filter_height_inch',
  styleField = 'filter_style',
  colorField = 'filter_color',
  odBaseField,
  odTopField,
  odField,
  defaultSortBy = 'name-asc',
}) => {
  const [sortBy, setSortBy] = useState<SortOption>(defaultSortBy);
  const [styleFilter, setStyleFilter] = useState<string>(ALL_VALUE);
  const [colorFilter, setColorFilter] = useState<string>(ALL_VALUE);

  const styleOptions = useMemo(() => uniqueTrimmedValues(tableData, styleField), [tableData, styleField]);
  const colorOptions = useMemo(() => uniqueTrimmedValues(tableData, colorField), [tableData, colorField]);

  const legendEntries = useMemo(() => {
    const entries = [
      { abbr: 'ID', label: 'Inlet Diameter' },
      { abbr: 'H', label: 'Height' },
    ];
    if (odBaseField) entries.push({ abbr: 'OD-B', label: 'Outside Diameter (Base)' });
    if (odTopField) entries.push({ abbr: 'OD-T', label: 'Outside Diameter (Top)' });
    if (odField) entries.push({ abbr: 'OD', label: 'Outside Diameter' });
    return entries;
  }, [odBaseField, odTopField, odField]);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      const style = String(row[styleField] ?? '').trim();
      const color = String(row[colorField] ?? '').trim();
      const matchesStyle = styleFilter === ALL_VALUE || style === styleFilter;
      const matchesColor = colorFilter === ALL_VALUE || color === colorFilter;
      return matchesStyle && matchesColor;
    });
  }, [tableData, styleField, colorField, styleFilter, colorFilter]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return parseInches(a.price) - parseInches(b.price);
        case 'price-desc':
          return parseInches(b.price) - parseInches(a.price);
        case 'height-asc':
          return parseInches(a[heightField]) - parseInches(b[heightField]);
        case 'diameter-asc':
          return parseInches(a[diameterField]) - parseInches(b[diameterField]);
        case 'od-asc':
          return odField ? parseInches(a[odField]) - parseInches(b[odField]) : 0;
        case 'name-asc':
        default:
          return String(a.title ?? '').localeCompare(String(b.title ?? ''));
      }
    });
    return sorted;
  }, [filteredData, sortBy, heightField, diameterField, odField]);

  return (
    <GridWrapper>
      <Toolbar>
        <SortControl>
          <label htmlFor="filter-sort">Sort by</label>
          <select id="filter-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
            {(Object.keys(SORT_LABELS) as SortOption[])
              .filter((option) => option !== 'od-asc' || odField)
              .map((option) => (
                <option key={option} value={option}>
                  {SORT_LABELS[option]}
                </option>
              ))}
          </select>
        </SortControl>

        {styleOptions.length > 1 && (
          <ChipRow>
            <ChipLabel>Style:</ChipLabel>
            <Chip $active={styleFilter === ALL_VALUE} onClick={() => setStyleFilter(ALL_VALUE)}>
              All
            </Chip>
            {styleOptions.map((option) => (
              <Chip key={option} $active={styleFilter === option} onClick={() => setStyleFilter(option)}>
                {option}
              </Chip>
            ))}
          </ChipRow>
        )}

        {colorOptions.length > 1 && (
          <ChipRow>
            <ChipLabel>Color:</ChipLabel>
            <Chip $active={colorFilter === ALL_VALUE} onClick={() => setColorFilter(ALL_VALUE)}>
              All
            </Chip>
            {colorOptions.map((option) => (
              <Chip key={option} $active={colorFilter === option} onClick={() => setColorFilter(option)}>
                {option}
              </Chip>
            ))}
          </ChipRow>
        )}
      </Toolbar>

      <LegendRow>
        {legendEntries.map(({ abbr, label }, index) => (
          <React.Fragment key={abbr}>
            {index > 0 && ' · '}
            <strong>{abbr}</strong> = {label}
          </React.Fragment>
        ))}
      </LegendRow>

      <ResultCount>
        {sortedData.length} filter{sortedData.length === 1 ? '' : 's'}
      </ResultCount>

      <Grid>
        {sortedData.map((row) => (
          <FilterCard
            key={row.id}
            row={row}
            diameterField={diameterField}
            heightField={heightField}
            styleField={styleField}
            colorField={colorField}
            odBaseField={odBaseField}
            odTopField={odTopField}
            odField={odField}
          />
        ))}
      </Grid>
    </GridWrapper>
  );
};

export default FilterCardGrid;

interface FilterCardProps {
  row: FilterProduct;
  diameterField: string;
  heightField: string;
  styleField: string;
  colorField: string;
  odBaseField?: string;
  odTopField?: string;
  odField?: string;
}

const FilterCard: React.FC<FilterCardProps> = ({
  row,
  diameterField,
  heightField,
  styleField,
  colorField,
  odBaseField,
  odTopField,
  odField,
}) => {
  const router = useRouter();
  const diameter = String(row[diameterField] ?? '').trim();
  const height = String(row[heightField] ?? '').trim();
  const style = String(row[styleField] ?? '').trim();
  const color = String(row[colorField] ?? '').trim();
  const odBase = odBaseField ? String(row[odBaseField] ?? '').trim() : '';
  const odTop = odTopField ? String(row[odTopField] ?? '').trim() : '';
  const od = odField ? String(row[odField] ?? '').trim() : '';
  const detailHref = `/store/filter/${row.gfu_part_num}`;
  const [cartUnavailable, setCartUnavailable] = useState(false);
  const canAddToCart = Boolean(row.buy_url) && !cartUnavailable;

  const colorParts = color ? color.split(' - ').map((part) => part.trim()).filter(Boolean) : [];
  const visibleColors = colorParts.slice(0, MAX_VISIBLE_COLORS);
  const extraColors = colorParts.slice(MAX_VISIBLE_COLORS);

  return (
    <Card onClick={() => router.push(detailHref)}>
      <ImageContainer>
        <ProductImage
          src={getProductImageUrl(row)}
          alt={row.title}
          onError={(e) => handleImageError(e)}
        />
      </ImageContainer>

      <Title>{row.title}</Title>

      {(diameter || height) && (
        <SpecLine>
          {diameter && <span>ID {diameter}</span>}
          {diameter && height && <span> &middot; </span>}
          {height && <span>H {height}</span>}
        </SpecLine>
      )}

      {(odBase || odTop) && (
        <SpecLine>
          {odBase && <span>OD-B {odBase}</span>}
          {odBase && odTop && <span> &middot; </span>}
          {odTop && <span>OD-T {odTop}</span>}
        </SpecLine>
      )}

      {od && (
        <SpecLine>
          <span>OD {od}</span>
        </SpecLine>
      )}

      {colorParts.length > 1 ? (
        <>
          {style && <SpecLine>{style}</SpecLine>}
          <ColorChipRow onClick={(e) => e.stopPropagation()}>
            {visibleColors.map((part) => (
              <ColorChip key={part}>{part}</ColorChip>
            ))}
            {extraColors.length > 0 && (
              <ColorChip $muted title={extraColors.join(', ')}>
                +{extraColors.length} more
              </ColorChip>
            )}
          </ColorChipRow>
        </>
      ) : (
        (style || color) && (
          <SpecLine>
            {style && <span>{style}</span>}
            {style && color && <span> &middot; </span>}
            {color && <span>{color}</span>}
          </SpecLine>
        )
      )}

      {/* Shopify's own price is the source of truth (matches checkout exactly). The DB-sourced
          row.price is only a fallback for rows with no linked Shopify buy_url. */}
      {!canAddToCart && <Price>{formatPriceWithCurrency(row.price ?? 0)}</Price>}

      <CTARow onClick={(e) => e.stopPropagation()}>
        <Button1 isLink to={detailHref} text="View Details" />
        {canAddToCart && (
          <AddToCartSlot>
            {/* Lazily mounts Shopify's own product/cart/toggle widget (same one used on
                product detail pages) only once the card nears the viewport, so a 150-item
                grid doesn't fire 150 Storefront API calls on page load. Using Shopify's own
                component here (instead of a custom cart) means adds correctly merge into the
                same cart/drawer shown on product pages, for free. */}
            <LazySection rootMargin="400px">
              <AddToCart
                buyButtonId={row.buy_url as string}
                id={row.gfu_part_num}
                showOptions={false}
                hidePrice={false}
                priceSize="18px"
                compareAtSize="13px"
                compact
                onError={() => setCartUnavailable(true)}
              />
            </LazySection>
          </AddToCartSlot>
        )}
      </CTARow>
    </Card>
  );
};

const GridWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 40px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.md} 0;
  border-bottom: 1px solid ${COLORS.border};
  margin-bottom: ${SPACING.lg};
`;

const SortControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};

  label {
    ${TYPOGRAPHY.label}
    color: ${COLORS.textSecondary};
  }

  select {
    ${TYPOGRAPHY.bodySM}
    padding: 8px 12px;
    border: 1px solid ${COLORS.border};
    border-radius: 6px;
    background: ${COLORS.white};
    color: ${COLORS.textPrimary};
    cursor: pointer;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.xs};
`;

const ChipLabel = styled.span`
  ${TYPOGRAPHY.label}
  color: ${COLORS.textSecondary};
`;

const Chip = styled.button<{ $active: boolean }>`
  ${TYPOGRAPHY.bodyXS}
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${(props) => (props.$active ? COLORS.brandGreen : COLORS.border)};
  background: ${(props) => (props.$active ? COLORS.brandGreen : COLORS.white)};
  color: ${(props) => (props.$active ? COLORS.white : COLORS.textPrimary)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
  }
`;

const LegendRow = styled.div`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin-bottom: ${SPACING.sm};

  strong {
    color: ${COLORS.textPrimary};
  }
`;

const ResultCount = styled.div`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin-bottom: ${SPACING.md};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${SPACING.xl};

  ${media.tablet} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${SPACING.lg};
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: ${SPACING.md};
  }
`;

const Card = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  overflow: hidden;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${COLORS.brandGreen};
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.bgDark};
  padding: ${SPACING.lg};

  ${Card}:hover & {
    background: ${COLORS.white};
  }

  ${media.mobile} {
    height: 180px;
    padding: ${SPACING.md};
  }
`;

const ProductImage = styled.img.attrs({ loading: 'lazy' })`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const Title = styled.h3`
  ${TYPOGRAPHY.h5}
  color: ${COLORS.textPrimary};
  margin: 0;
  padding: ${SPACING.md} ${SPACING.md} 0;
`;

const SpecLine = styled.div`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  padding: 4px ${SPACING.md} 0;
`;

const ColorChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  padding: 6px ${SPACING.md} 0;
`;

const ColorChip = styled.span<{ $muted?: boolean }>`
  ${TYPOGRAPHY.bodyXS}
  padding: 2px 8px;
  border-radius: 10px;
  background: ${(props) => (props.$muted ? COLORS.white : COLORS.bgDark)};
  border: 1px solid ${COLORS.border};
  color: ${COLORS.textSecondary};
  white-space: nowrap;
`;

const Price = styled.div`
  ${TYPOGRAPHY.h4}
  color: ${COLORS.brandGreen};
  padding: ${SPACING.sm} ${SPACING.md} 0;
`;

const CTARow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: ${SPACING.xs};
  padding: ${SPACING.md};
  margin-top: auto;

  > a button {
    flex: 1;
    max-width: 140px;
    font-size: 14px;
    text-transform: uppercase;
  }
`;

const AddToCartSlot = styled.div`
  flex: 1;
  max-width: 140px;
  min-height: 32px;
`;
